import React, { useState, useEffect, useMemo } from 'react';
import client from './api/client';
import PageHeader from './components/PageHeader';
import CollapsiblePanel from './components/CollapsiblePanel';
import { SP_PER_EXTRACTION, EXTRACTOR_NES_TIERS, TYPE_ID_LARGE_SKILL_INJECTOR, TYPE_ID_SKILL_EXTRACTOR } from './utils/eveConstants';
import { formatPrice } from './utils/formatPrice';
import { useFeeSettings } from './hooks/useFeeSettings';
import FeeSettingsInputs from './components/FeeSettingsInputs';
import './components/shared.css';
import './SkillFarmPage.css';

const SP_PER_MONTH = 1_944_000;       // per character with optimal attributes
const BONUS_SP = 100_000;             // 1-month Omega only, per account

const OMEGA_TIERS = [
  { months: 1,  plex: 500 },
  { months: 3,  plex: 1200 },
  { months: 6,  plex: 2100 },
  { months: 12, plex: 3600 },
  { months: 24, plex: 6600 },
];

const MPTC_TIERS = [
  { qty: 1,  plex: 350 },
  { qty: 2,  plex: 600 },
  { qty: 3,  plex: 800 },
  { qty: 6,  plex: 1500 },
  { qty: 12, plex: 2700 },
  { qty: 24, plex: 4600 },
];

function SkillFarmPage() {
  const [prices, setPrices] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Configuration
  const [characters, setCharacters] = useState(3);

  // Fee settings (shared hook with auto-save)
  const extraSaveFields = useMemo(() => ({ characters }), [characters]);
  const {
    brokerFee, setBrokerFee,
    salesTax, setSalesTax,
    sccBuy, setSccBuy,
    sccSell, setSccSell,
    buyCostMultiplier,
    sellRevenueMultiplier,
  } = useFeeSettings({
    autoSave: true,
    extraSaveFields,
    onLoad: data => setCharacters(data.characters),
  });

  // Table controls
  const [tableExpanded, setTableExpanded] = useState(false);
  const [showProfitableOnly, setShowProfitableOnly] = useState(false);

  // Load prices on mount
  useEffect(() => {
    const load = async () => {
      try {
        const [jitaRes, plexRes] = await Promise.all([
          client.get(`/eve/prices/?type_ids=${TYPE_ID_LARGE_SKILL_INJECTOR},${TYPE_ID_SKILL_EXTRACTOR}`),
          client.get('/eve/prices/plex/'),
        ]);
        setPrices({
          largeSkillInjector: jitaRes.data.prices[`${TYPE_ID_LARGE_SKILL_INJECTOR}`],
          skillExtractor: jitaRes.data.prices[`${TYPE_ID_SKILL_EXTRACTOR}`],
          plex: plexRes.data,
        });
        setError(null);
      } catch (err) {
        console.error('Error fetching skill farm prices:', err);
        setError('Failed to load prices. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Calculate profitability for all tier combinations
  const combinations = useMemo(() => {
    if (!prices) return [];

    const injectorPrice = prices.largeSkillInjector?.sell_price;
    const extractorMarketPrice = prices.skillExtractor?.sell_price;
    const plexPrice = prices.plex?.sell_price;
    if (!injectorPrice || !extractorMarketPrice || !plexPrice) return [];

    const plexBuyCost = plexPrice * buyCostMultiplier;

    // Build extractor options: market + each NES tier
    const extractorOptions = [
      { label: 'Market', plexPerExtractor: null, iskPerExtractor: extractorMarketPrice * buyCostMultiplier },
      ...EXTRACTOR_NES_TIERS.map(t => ({
        label: `NES ${t.qty}x (${t.plex} PLEX)`,
        plexPerExtractor: t.plex / t.qty,
        iskPerExtractor: (t.plex / t.qty) * plexBuyCost,
      })),
    ];

    const mptcTiers = characters > 1 ? MPTC_TIERS : [null];
    const results = [];

    for (const omega of OMEGA_TIERS) {
      const omegaPlexPerMonth = omega.plex / omega.months;
      const bonusSp = omega.months === 1 ? BONUS_SP : 0;
      const totalSp = (characters * SP_PER_MONTH) + bonusSp;
      const extractions = totalSp / SP_PER_EXTRACTION;

      for (const mptc of mptcTiers) {
        const mptcsNeeded = characters - 1;
        const mptcPlexPerMonth = mptc ? (mptc.plex / mptc.qty) * mptcsNeeded : 0;

        const subscriptionIsk = (omegaPlexPerMonth + mptcPlexPerMonth) * plexBuyCost;
        const revenue = extractions * injectorPrice * sellRevenueMultiplier;

        for (const ext of extractorOptions) {
          const extractorCost = ext.iskPerExtractor * extractions;
          const totalCost = subscriptionIsk + extractorCost;
          const profit = revenue - totalCost;

          results.push({
            omegaLabel: `${omega.months}mo (${omega.plex.toLocaleString()} PLEX)`,
            omegaMonths: omega.months,
            omegaPlexPerMonth,
            mptcLabel: mptc ? `${mptc.qty}x (${mptc.plex.toLocaleString()} PLEX)` : 'N/A',
            mptcPlexPerMonth,
            extractorLabel: ext.label,
            totalSp,
            extractions,
            revenue,
            subscriptionIsk,
            extractorCost,
            totalCost,
            profit,
          });
        }
      }
    }

    results.sort((a, b) => b.profit - a.profit);
    return results;
  }, [prices, characters, buyCostMultiplier, sellRevenueMultiplier]);

  const best = combinations[0] || null;

  return (
    <div className="page">
      <PageHeader title="Skill Farm" meta="Profit from extracting and selling skill points" />
      <div className="page-content">

        {loading && <div className="page-status">Loading prices...</div>}
        {error && <div className="page-status error">{error}</div>}

        {prices && (
          <>
            {/* Price Cards */}
            <div className="kpis">
              <div className="kpi">
                  <span>{prices.largeSkillInjector.name}</span>
                  <b>{formatPrice(prices.largeSkillInjector.sell_price)}</b>
                  <small>Jita Min Sell</small>
                </div>
              <div className="kpi">
                  <span>{prices.skillExtractor.name}</span>
                  <b>{formatPrice(prices.skillExtractor.sell_price)}</b>
                  <small>Jita Min Sell</small>
                </div>
              <div className="kpi">
                  <span>PLEX</span>
                  <b>{formatPrice(prices.plex.sell_price)}</b>
                  <small>Min Sell (Buying Cost)</small>
                </div>
              <div className="kpi">
                  <span>PLEX</span>
                  <b>{formatPrice(prices.plex.buy_price)}</b>
                  <small>Max Buy (Reference)</small>
                </div>
            </div>

            {/* Settings Panel */}
            <section className="panel">
              <div className="panel-head"><h2>Settings</h2></div>
              <div className="panel-body settings-row">
                <div className="settings-group">
                  <label>Characters per Account</label>
                  <select value={characters} onChange={e => setCharacters(Number(e.target.value))}>
                    <option value={1}>1</option>
                    <option value={2}>2</option>
                    <option value={3}>3</option>
                  </select>
                </div>
                <FeeSettingsInputs
                  brokerFee={brokerFee} setBrokerFee={setBrokerFee}
                  salesTax={salesTax} setSalesTax={setSalesTax}
                  sccBuy={sccBuy} setSccBuy={setSccBuy}
                  sccSell={sccSell} setSccSell={setSccSell}
                />
              </div>
            </section>

            {/* Best Combination */}
            {best && (
              <section className="panel">
                <div className="panel-head">
                  <h2>Most profitable combination</h2>
                  <span className={`pill ${best.profit >= 0 ? 'pill-pos' : 'pill-neg'}`}>
                    {best.profit >= 0 ? 'Profitable' : 'Loses ISK'}
                  </span>
                </div>
                <div className="panel-body best-combo-details">
                  <div className="best-combo-config">
                    <span>Omega: {best.omegaLabel}</span>
                    <span>MPTC: {best.mptcLabel}</span>
                    <span>Extractors: {best.extractorLabel}</span>
                  </div>
                  <div className="best-combo-numbers">
                    <div className="best-combo-line">
                      <span>SP/month</span>
                      <span>{best.totalSp.toLocaleString()}</span>
                    </div>
                    <div className="best-combo-line">
                      <span>Extractions/month</span>
                      <span>{best.extractions.toFixed(2)}</span>
                    </div>
                    <div className="best-combo-line revenue">
                      <span>Revenue (after fees)</span>
                      <span>{formatPrice(best.revenue)}</span>
                    </div>
                    <div className="best-combo-line">
                      <span>Subscription cost</span>
                      <span>-{formatPrice(best.subscriptionIsk)}</span>
                    </div>
                    <div className="best-combo-line">
                      <span>Extractor cost</span>
                      <span>-{formatPrice(best.extractorCost)}</span>
                    </div>
                    <div className={`best-combo-line profit ${best.profit >= 0 ? 'positive' : 'negative'}`}>
                      <span>Monthly Profit</span>
                      <span>{formatPrice(best.profit)}</span>
                    </div>
                  </div>
                </div>
              </section>
            )}

            {/* All Combinations Table */}
            {combinations.length > 0 && (
              <CollapsiblePanel
                title="All tier combinations"
                sub={`${combinations.length} combinations`}
                collapsed={!tableExpanded}
                onToggle={() => setTableExpanded(prev => !prev)}
                actions={(
                  <label className="check-label">
                    <input
                      type="checkbox"
                      checked={showProfitableOnly}
                      onChange={e => setShowProfitableOnly(e.target.checked)}
                    />
                    Profitable only
                  </label>
                )}
              >
                  <div className="table-scroll">
                    <table className="data-table combo-table">
                      <thead>
                        <tr>
                          <th className="num">#</th>
                          <th>Omega</th>
                          {characters > 1 && <th>MPTC</th>}
                          <th>Extractors</th>
                          <th className="num">Revenue</th>
                          <th className="num">Total cost</th>
                          <th className="num">Monthly profit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {combinations
                          .filter(c => !showProfitableOnly || c.profit > 0)
                          .map((combo, i) => (
                          <tr key={`${combo.omegaMonths}-${combo.mptcLabel}-${combo.extractorLabel}`} className={i === 0 ? 'best-row' : ''}>
                            <td className="num text-faint">{i + 1}</td>
                            <td>{combo.omegaLabel}</td>
                            {characters > 1 && <td>{combo.mptcLabel}</td>}
                            <td>{combo.extractorLabel}</td>
                            <td className="num">{formatPrice(combo.revenue)}</td>
                            <td className="num">{formatPrice(combo.totalCost)}</td>
                            <td className={`num ${combo.profit >= 0 ? 'text-pos' : 'text-neg'}`}>
                              {formatPrice(combo.profit)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
              </CollapsiblePanel>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default SkillFarmPage;
